import { notification } from 'antd';
import api from './api';

const MISSING_QUEUES_LIMIT = 10;

function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// В файл уезжают только эти четыре поля: id намеренно отбрасывается,
// файл описывает зону, а не её экземпляр на конкретном стенде.
export function buildZoneExport(zone) {
  return {
    name: zone?.name || '',
    longname: zone?.longname || '',
    description: zone?.description || '',
    config: zone?.config || { params: [], screens: [] },
  };
}

export function buildFileName(zoneName, date = new Date()) {
  const safeName = String(zoneName || '')
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  const datePart = formatDate(date);

  return safeName ? `zone-${safeName}-${datePart}.json` : `zone-${datePart}.json`;
}

export function downloadZone(zone) {
  const payload = JSON.stringify(buildZoneExport(zone), null, 2);
  const blob = new Blob([payload], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = buildFileName(zone?.name);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

// Глубже структура намеренно не проверяется: экраны и кнопки в конструкторе
// меняются свободно, жёсткая схема быстро начнёт отклонять валидные конфиги.
function isZoneData(data) {
  if (!isPlainObject(data)) return false;
  if (!isPlainObject(data.config)) return false;
  if (!Array.isArray(data.config.screens)) return false;
  if (!Array.isArray(data.config.params)) return false;
  return true;
}

// Форма ответа повторяет соглашение из utils/api/index.js: { status, data } / { status, errMessage }
export function readZoneFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onerror = () => {
      resolve({ status: false, errMessage: 'Не удалось прочитать файл' });
    };

    reader.onload = (e) => {
      let parsed;

      try {
        parsed = JSON.parse(e.target.result);
      } catch (err) {
        resolve({ status: false, errMessage: 'Не удалось прочитать файл: некорректный JSON' });
        return;
      }

      if (!isZoneData(parsed)) {
        resolve({ status: false, errMessage: 'Файл не является конфигурацией зоны обслуживания' });
        return;
      }

      resolve({ status: true, data: buildZoneExport(parsed) });
    };

    reader.readAsText(file);
  });
}

export function findMissingQueues(config, queueList) {
  const queues = Array.isArray(queueList) ? queueList : [];
  const missing = [];

  (config?.screens || []).forEach((screen) => {
    (screen?.buttons || []).forEach((button) => {
      const queueId = button?.params?.value;

      if (!queueId) return;
      if (queues.some((item) => item.id === queueId)) return;

      missing.push({
        screenTitle: screen.headTitle || screen.title || screen.screenId,
        buttonName: button.name || button.id,
        queueId,
      });
    });
  });

  return missing;
}

export function buildMissingQueuesText(missing) {
  const lines = missing
    .slice(0, MISSING_QUEUES_LIMIT)
    .map((item) => `${item.screenTitle} → ${item.buttonName} (id очереди ${item.queueId})`);

  if (missing.length > MISSING_QUEUES_LIMIT) {
    lines.push(`и ещё ${missing.length - MISSING_QUEUES_LIMIT}`);
  }

  return ['Привяжите очереди заново в конструкторе зоны:', ...lines].join('\n');
}

// Вызывается после успешного импорта. Импорт уже состоялся, поэтому ошибка
// вспомогательного запроса очередей тихо игнорируется — она не про импорт.
export async function warnAboutMissingQueues(config) {
  const res = await api.getQueue();

  if (!res?.status) return;

  const missing = findMissingQueues(config, res.data);

  if (missing.length === 0) return;

  notification.warning({
    message: 'Не найдены очереди для части кнопок',
    description: buildMissingQueuesText(missing),
    duration: 0,
    style: { whiteSpace: 'pre-line' },
  });
}
